import { reasonSlug } from '../comm-log/types';
import type { DispositionKey, Period } from './types';

/**
 * The complete link map (spec 19 §5). **Every clickable element on the Dashboard,
 * and where it goes** — replacing §8's general statement.
 *
 * Two rules run through all of it:
 *
 * 1. **The period carries through.** Every link into the Communication log passes
 *    the Dashboard's current window as `from`/`to`, so the row count the user
 *    lands on matches the number they clicked.
 * 2. **The always-now panels do not pass a period.** The schedule, the review
 *    queue and the uncovered reasons are true at this second, and scoping their
 *    destination would contradict §3.1.
 *
 * Every `/communications?direction=…` link depends on the Communication log
 * amendment (spec 18), which adds the direction filter, the seven inbound
 * dispositions and URL-driven filters. Shipped against the old log these would
 * resolve to an unfiltered page — **worse than not linking at all** — so the two
 * changes land together.
 */

/** The log's `range` values, which the Dashboard's two periods map onto exactly. */
export function periodRange(period: Period): string {
  return period === '30d' ? '30d' : '24h';
}

function log(params: Record<string, string | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') p.set(k, v);
  }
  return `/communications?${p.toString()}`;
}

/** Conversations handled → every inbound contact in the window. */
export function conversationsLink(period: Period): string {
  return log({ direction: 'inbound', range: periodRange(period) });
}

/**
 * A disposition row → the same rows, filtered to that disposition.
 *
 * The Dashboard's disposition keys and the log's `verdict` values are **the same
 * strings** — a shared contract across the two surfaces, not two lists that happen
 * to match today (§7.3).
 */
export function dispositionLink(period: Period, key: DispositionKey): string {
  return log({ direction: 'inbound', verdict: key, range: periodRange(period) });
}

/** A reason bar → the bookings behind it, by the slug both surfaces share (§7.4). */
export function reasonLink(period: Period, reason: string): string {
  return log({
    direction: 'inbound',
    verdict: 'booked',
    reason: reasonSlug(reason),
    range: periodRange(period),
  });
}

/** Opted out → the outbound contacts consent suppressed. */
export function optedOutLink(period: Period): string {
  return log({ direction: 'outbound', verdict: 'suppressed', reason: 'consent', range: periodRange(period) });
}

/** Held by the contact cap → the outbound contacts a rule stopped. */
export function heldByCapLink(period: Period): string {
  return log({ direction: 'outbound', verdict: 'suppressed', range: periodRange(period) });
}

/** Fixed destinations — no period, because none of these is a windowed list. */
export const APPOINTMENT_REASONS = '/settings/appointment-reasons';
export const TRIAGE_INBOX = '/triage';
export const TRAIN_ELVA = '/settings/ai/train';

/** A review row opens the queue it counts. Always now, so no period. */
export function reviewRowLink(queue: string): string {
  return `${TRAIN_ELVA}?tab=${encodeURIComponent(queue)}`;
}

/** A schedule day opens the calendar on that day. Always now, so no period. */
export function scheduleDayLink(date: string): string {
  return `/calendar?date=${encodeURIComponent(date)}`;
}
